import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { OPPORTUNITY_SUBMISSION_FORM } from '@app/apollo/queries';
import { TOGGLE_FORM_VISIBILITY } from '@app/apollo/mutations';
import { useTranslation } from '@app/config/i18n';
import { FIELDS_ENUM, APPLICATION_STATUS } from '@app/constants';
import moment from 'moment';
import { Grid, Popover, Tooltip, Zoom, Typography, IconButton } from '@mui/material';
import Link from "@app/components/atoms/Link";
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';

const getStatusColor = (status) => {
  if (status === APPLICATION_STATUS.REVIEWED || status === APPLICATION_STATUS.UNDER_REVIEW) {
    return '#FAB32B';
  }
  if (status === APPLICATION_STATUS.ACCEPTED) {
    return '#67BF4E';
  }
  if (status === APPLICATION_STATUS.REJECTED) {
    return '#E75055';
  }
  return 'primary';
};

// Helper function to parse JSON labels safely
const parseLabel = (labelJson) => {
  try {
    if (!labelJson) return '';
    const label = JSON.parse(labelJson);
    return label.en || Object.values(label)[0] || '';
  } catch (e) {
    return labelJson || '';
  }
};

// Helper function to get full name from member data
const takeFullName = (member) => {
  const fullName = member.filter((el) => el.type === FIELDS_ENUM.TEXT_FIELD)[0].value;
  return fullName;
};

export const useSubmissionForm = ({
  opportunity,
  isOrganizer = false,
}) => {
  const { t } = useTranslation(['common']);
  const [columns, setColumns] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  // Add mutation for toggling form visibility
  const [toggleFormVisibility, { loading: updatingSubmissionForm }] = useMutation(TOGGLE_FORM_VISIBILITY);
  // Fetch submission form data
  const {
    data: { opportunitySubmissionForm } = {},
    loading: opportunitySubmissionFormLoading,
    refetch: refetchOpportunitySubmissionForm,
  } = useQuery(OPPORTUNITY_SUBMISSION_FORM, {
    variables: {
      opportunity: opportunity?.id,
    },
    skip: !opportunity?.id,
  });

  const staticColumns = [
    {
      id: 'status',
      numeric: false,
      disablePadding: false,
      label: t('common:Status'),
      isDynamic: false,
      isVisible: true,
      fieldType: 'status'
    },
    {
      id: 'registration_status',
      numeric: false,
      disablePadding: false,
      label: t('common:Registration Status'),
      isDynamic: true,
      isVisible: true,
      fieldType: 'registration_status'
    },
    {
      id: 'payment_status',
      numeric: false,
      disablePadding: false,
      label: t('common:Payment Status'),
      isDynamic: true,
      isVisible: true,
      fieldType: 'payment_status'
    },
    {
      id: 'created_at',
      numeric: false,
      disablePadding: false,
      label: t('common:Date'),
      isDynamic: false,
      isVisible: true,
      fieldType: 'date'
    },
  ];

  // Generate columns based on form fields
  useEffect(() => {
    let fields = [];
    if (opportunitySubmissionForm?.fields && opportunitySubmissionForm.fields.length > 0) {
      fields = opportunitySubmissionForm.fields.map((field, index) => ({
        id: field.id,
        numeric: false,
        disablePadding: false,
        label: parseLabel(field.label),
        fieldType: field.type,
        fieldSubtype: field.subtype,
        isDynamic: true,
        isVisible: index < 3,
      }));
    }

    const cols = [...fields, ...staticColumns];
    setColumns(cols);
  }, [opportunitySubmissionForm]);

  // Handle column visibility
  const handleToggleColumn = (columnId) => {
    setColumns(prevColumns =>
      prevColumns.map(col => col.id === columnId ? { ...col, isVisible: !col.isVisible } : col)
    );
  };

  // Generate columns based on form fields
  const tableCols = useMemo(() => {
    const tableHeaderRow = columns?.filter((field) => field.isVisible).map((field) => {
      // Handle Title fields
      if (field.fieldType === FIELDS_ENUM.TITLE || field.fieldType === FIELDS_ENUM.MANDATORY_TITLE) {
        return {
          title: field.label,
          type: field.fieldType,
          render: (rowData) => {
            const matchingElement = rowData.elements.find(
              (element) => element.id === field.id || (element.field_id && element.field_id === field.id)
            );

            if (matchingElement?.value) {
              const {value} = matchingElement;
              return value.length > 40 ? (
                <Tooltip
                  title={value}
                  arrow
                  placement="top"
                  TransitionComponent={Zoom}
                  TransitionProps={{ timeout: 300 }}
                  leaveDelay={500}
                >
                  <div>
                    <Link
                      style={{ fontSize: '14px' }}
                      color="secondary"
                      href="view_submission"
                      params={{ slug: opportunity.slug, id: rowData.id }}
                    >
                      {value.substring(0, 41)}...
                    </Link>
                  </div>
                </Tooltip>
              ) : (
                <Link
                  style={{ fontSize: '14px' }}
                  color="secondary"
                  href="view_submission"
                  params={{ slug: opportunity.slug, id: rowData.id }}
                >
                  {value}
                </Link>
              );
            }
            return null;
          }
        };
      }

      // Handle Authors fields
      if (field.fieldType === FIELDS_ENUM.MANDATORY_AUTHORS) {
        return {
          title: field.label,
          type: field.fieldType,
          render: (rowData) => {
            const matchingElement = rowData.elements.find(
              (element) => element.id === field.id || (element.field_id && element.field_id === field.id)
            );

            if (matchingElement?.child_elements?.length > 0) {
              return (
                <Grid container xs={12}>
                  <Grid item md={6} textAlign="start">
                    <Typography variant="caption" color="secondary">
                      {matchingElement.child_elements[0][0].value}
                    </Typography>
                  </Grid>
                  <Grid item md={6} textAlign="center">
                    <Typography
                      aria-owns={anchorEl ? 'mouse-over-popover' : undefined}
                      aria-haspopup="true"
                      onMouseEnter={(e) => {
                        setSelectedOpportunity(rowData.id);
                        setAnchorEl(e.currentTarget);
                      }}
                      onMouseLeave={() => setAnchorEl(null)}
                      variant="caption"
                      color="primary"
                    >
                      {matchingElement.child_elements.length > 1 && `+${matchingElement.child_elements.length - 1}`}
                    </Typography>
                    {rowData.id === selectedOpportunity && (
                      <Popover
                        id="mouse-over-popover"
                        sx={{ pointerEvents: 'none' }}
                        open={Boolean(anchorEl)}
                        anchorEl={anchorEl}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        onClose={() => setAnchorEl(null)}
                        disableRestoreFocus
                      >
                        {matchingElement.child_elements.map((child, index) => {
                          if (index !== 0) {
                            return (
                              <Typography
                                key={child.id}
                                variant="inherit"
                                color="secondary"
                                sx={{ px: 1.5, py: 0.5, fontSize: '14px' }}
                              >
                                {takeFullName(child)}
                              </Typography>
                            );
                          }
                          return null;
                        })}
                      </Popover>
                    )}
                  </Grid>
                </Grid>
              );
            }
            return null;
          }
        };
      }

      // Handle Abstract fields
      if (field.fieldType === FIELDS_ENUM.ABSTRACT || field.fieldType === FIELDS_ENUM.MANDATORY_ABSTRACT) {
        return {
          title: field.label,
          type: field.fieldType,
          render: (rowData) => {
            const matchingElement = rowData.elements.find(
              (element) => element.id === field.id || (element.field_id && element.field_id === field.id)
            );
            return matchingElement?.value ? <CheckIcon color="secondary" /> : <CloseIcon color="secondary" />;
          }
        };
      }

      // Handle Papers fields
      if (field.fieldType === FIELDS_ENUM.PAPER || field.fieldType === FIELDS_ENUM.MANDATORY_PAPER) {
        return {
          title: field.label,
          type: field.fieldType,
          isVisible: field.isVisible,
          render: (rowData) => {
            const matchingElement = rowData.elements.find(
              (element) => element.id === field.id || (element.field_id && element.field_id === field.id)
            );
            return matchingElement?.files?.length > 0 ? <CheckIcon color="secondary" /> : <CloseIcon color="secondary" />;
          }
        };
      }

      // Handle Status field
      if (field.fieldType === 'status') {
        return {
          title: field.label,
          type: field.fieldType,
          isVisible: field.isVisible,
          render: (rowData) => (
            <Typography
              variant="caption"
              color={getStatusColor(rowData.status)}
            >
              {rowData.status === APPLICATION_STATUS.UNDER_REVIEW ? 'Under Review' : rowData.status}
            </Typography>
          )
        };
      }

      // Handle Date field
      if (field.fieldType === 'date') {
        return {
          title: field.label,
          type: field.fieldType,
          isVisible: field.isVisible,
          render: (rowData) => (
            <Typography variant="caption" color="secondary">
              {moment(rowData.created_at).format('DD MMM YYYY').toUpperCase()}
            </Typography>
          )
        };
      }

      // Handle Payment Status field
      if (field.fieldType === 'payment_status') {
        return {
          title: field.label,
          type: field.fieldType,
          isVisible: field.isVisible,
          render: (rowData) => {
            const approvalType = rowData?.opportunity?.approval_type;
            const regStatus = rowData?.status;
            const paymentStatus = rowData?.payment_status;
            let paymentStatusText = '';

            if (paymentStatus) {
              if (approvalType === 'AUTO' || regStatus === 'APPROVED') {
                paymentStatusText = paymentStatus === 'SUCCEEDED' ? 'PAID' : 'UNPAID';
              } else {
                paymentStatusText = t('common:NO ACTION');
              }
            } else {
              paymentStatusText = t('common:FREE');
            }

            return (
              <Typography variant="caption" color="secondary">
                {paymentStatusText}
              </Typography>
            );
          }
        };
      }

      // Handle custom fields
      return {
        title: field.label,
        type: field.fieldType,
        render: (rowData) => {
          const matchingElement = rowData.elements.find(
            (element) => element.id === field.id || (element.field_id && element.field_id === field.id)
          );

          if (matchingElement) {
            if (matchingElement.files && matchingElement.files.length > 0) {
              return <CheckIcon color="secondary" />;
            }

            if (typeof matchingElement.value === 'boolean') {
              return matchingElement.value ? (
                <CheckIcon color="secondary" />
              ) : (
                <CloseIcon color="secondary" />
              );
            }

            if (matchingElement.value) {
              return (
                <Typography variant="caption" color="secondary">
                  {String(matchingElement.value).length > 30
                    ? `${String(matchingElement.value).substring(0, 27)}...`
                    : String(matchingElement.value)}
                </Typography>
              );
            }

            return <CloseIcon color="secondary" />;
          }

          return null;
        },
      };
    });

    // Add action column for user view or organizer view
    if (isOrganizer) {
      tableHeaderRow.push({
        title: t('common:actions'),
        field: 'actions',
        render: (rowData) => (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Tooltip title={t('common:View submission')}>
              <Link
                href="view_submission"
                params={{ slug: opportunity.slug, id: rowData.id }}
              >
                <IconButton
                  size="small"
                  sx={{ color: 'primary.main' }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Link>
            </Tooltip>

          </div>
        ),
      });
    }

    return tableHeaderRow;
  }, [columns, t, opportunity?.slug, anchorEl, selectedOpportunity, isOrganizer]);

  const toggleSubmissionFormVisibility = async () => {
    try {
      await toggleFormVisibility({
        variables: {
          id: opportunity.id,
          input: {
            eventSlug: opportunity.slug,
            formType: 'submission',
            isActive: !opportunitySubmissionForm.is_active,
          }
        }
      });
      refetchOpportunitySubmissionForm();
    } catch (error) {
      console.error('Error toggling form visibility:', error);
    }
  };

  return {
    columns,
    tableCols,
    staticColumns,
    handleToggleColumn,
    updatingSubmissionForm,
    opportunitySubmissionForm,
    opportunitySubmissionFormLoading,
    refetchOpportunitySubmissionForm,
    toggleSubmissionFormVisibility,
  };
};
